// src/pages/ChhathPujaHome.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../../redux/actions/authActions";

const ChhathPujaHome = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Chhath Knowledge Base");
  const [language, setLanguage] = useState("en"); // "en" or "hi"

  // Load saved language from localStorage
  useEffect(() => {
    const savedLang = localStorage.getItem("chhathLanguage") || "en";
    setLanguage(savedLang);
    setActiveTab(savedLang === "en" ? "Chhath Knowledge Base" : "छठ ज्ञान भंडार");
  }, []);

  const toggleLanguage = () => {
    const newLang = language === "en" ? "hi" : "en";
    setLanguage(newLang);
    localStorage.setItem("chhathLanguage", newLang);
    
    // Reset active tab when language changes
    setActiveTab(newLang === "en" ? "Chhath Knowledge Base" : "छठ ज्ञान भंडार");
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  // Translations
  const translations = {
    en: {
      title: "🌅🙏🪔 ChhathiMaiya",
      subtitle: "Surya Arghya • Chhathi Maiya Ki Kripa • Poorvanchal se Vishwa Tak",
      tagline: "Free Platform for End-to-End Chhath Puja Celebration and Management",
      welcome: "Welcome,",
      login: "Login",
      logout: "Logout",
      about: "About Chhath",
      liveDates: "Live Dates 2026",
      chhathTube: "ChhathTube",
      updates: "Updates",
      faq: "Puja FAQ",
      askAI: "Ask Chhath AI",
      description: "Millions celebrate Chhath Puja in Bihar, East UP, Jharkhand — now spreading across India & the world!",
      footerText: "© 2025 Think5Whys Global DeepTech Solutions. Jai Surya Dev!",
      aboutLink: "About ChhathPuja",
      noItems: "No items in this category.",
    },
    hi: {
      title: "🌅🙏🪔 छठीमइया",
      subtitle: "सूर्य अर्घ्य • छठी मइया की कृपा • पूर्वांचल से विश्व तक",
      tagline: "छठ पूजा के पूर्ण उत्सव और प्रबंधन के लिए निःशुल्क प्लेटफॉर्म",
      welcome: "स्वागत है,",
      login: "लॉगिन",
      logout: "लॉगआउट",
      about: "छठ के बारे में",
      liveDates: "2026 लाइव तिथियाँ",
      chhathTube: "छठट्यूब",
      updates: "अपडेट्स",
      faq: "पूजा FAQ",
      askAI: "छठ AI से पूछें",
      description: "बिहार, पूर्वी उत्तर प्रदेश, झारखंड में लाखों लोग छठ पूजा मनाते हैं — अब पूरे भारत और विश्व में फैल रही है!",
      footerText: "© 2025 थिंक5व्हाईस ग्लोबल डीपटेक सॉल्यूशंस। जय सूर्य देव!",
      aboutLink: "छठ पूजा के बारे में",
      noItems: "इस श्रेणी में कोई आइटम नहीं है।",
    },
  };

  const t = translations[language];

  // Tab Groups - Bilingual
  const groupNames = [
    { labelEn: "Chhath Knowledge Base", labelHi: "छठ ज्ञान भंडार", emoji: "📜" },
    { labelEn: "Puja Management", labelHi: "पूजा प्रबंधन", emoji: "🛠️" },
    { labelEn: "Arghya & Rituals", labelHi: "अर्घ्य एवं अनुष्ठान", emoji: "🌞" },
    { labelEn: "Thekua & Prasadam", labelHi: "ठेकुआ एवं प्रसाद", emoji: "🍪" },
    { labelEn: "Community Sangam", labelHi: "समुदाय संगम", emoji: "🤝" },
    { labelEn: "Chronicles & Kahani", labelHi: "वृत्तांत एवं कहानी", emoji: "📖" },
    { labelEn: "Chhath Marketplace", labelHi: "छठ मार्केटप्लेस", emoji: "🛍️" },
    { labelEn: "ChhathTube", labelHi: "छठट्यूब", emoji: "📺" },
    { labelEn: "Global Chhath", labelHi: "वैश्विक छठ", emoji: "🌍" },
    { labelEn: "Festival Calendar", labelHi: "त्योहार कैलेंडर", emoji: "📅" },
    { labelEn: "Live Darshan & Events", labelHi: "लाइव दर्शन एवं आयोजन", emoji: "🙏" },
    { labelEn: "Recognition & Progress", labelHi: "सम्मान एवं प्रगति", emoji: "🏅" },
    { labelEn: "Analytics & Insights", labelHi: "विश्लेषण एवं अंतर्दृष्टि", emoji: "📊" },
    { labelEn: "Employment & Income", labelHi: "रोजगार एवं आय", emoji: "💰" },
  ];

  // All Menu Buttons with Hindi Translations
  const menuButtons1 = [
    // 📜 Chhath Knowledge Base
    { labelEn: "History of Chhath Puja", labelHi: "छठ पूजा का इतिहास", emoji: "📖", link: "/chhathpuja-knowledge/history", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Chhathi Maiya Ki Katha", labelHi: "छठी मइया की कथा", emoji: "🙏", link: "/chhathpuja-knowledge/chhathi-maiya", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Vedic & Puranic Significance", labelHi: "वैदिक एवं पुराणिक महत्व", emoji: "🕉️", link: "/chhathpuja-knowledge/vedic", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Poorvanchal Traditions (Bihar, East UP, Jharkhand)", labelHi: "पूर्वांचल परंपराएँ (बिहार, पूर्वी UP, झारखंड)", emoji: "🏞️", link: "/chhathpuja-knowledge/poorvanchal", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Scientific & Health Benefits", labelHi: "वैज्ञानिक एवं स्वास्थ्य लाभ", emoji: "🔬", link: "/chhathpuja-knowledge/science", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Chhath Bhajans & Bhojpuri Geet", labelHi: "छठ भजन एवं भोजपुरी गीत", emoji: "🎵", link: "/chhathpuja-knowledge/bhajans", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Mythological Stories", labelHi: "पौराणिक कथाएँ", emoji: "📜", link: "/chhathpuja-knowledge/myths", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Ritual Steps Explained", labelHi: "अनुष्ठान चरण विस्तार से", emoji: "📋", link: "/chhathpuja-knowledge/rituals-guide", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Chhath for Gen Z & Kids", labelHi: "Gen Z और बच्चों के लिए छठ", emoji: "🧒🌅", link: "/chhathpuja-knowledge/genz-kids", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Gen Z & Kids Fun Activities & Challenges", labelHi: "Gen Z और बच्चों के लिए मज़ेदार गतिविधियाँ और चुनौतियाँ", emoji: "🎯🎨", link: "/chhathpuja-knowledge/fun-activities", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Gen Z & Kids Reels, Stories & Digital Awareness", labelHi: "Gen Z और बच्चों के लिए रील्स, स्टोरी और डिजिटल जागरूकता", emoji: "📱🎥", link: "/chhathpuja-knowledge/digital-awareness", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Chhath Values & Life Lessons for Gen Z & Kids", labelHi: "Gen Z और बच्चों के लिए छठ के मूल्य और जीवन सीख", emoji: "🧠✨", link: "/chhathpuja-knowledge/values-lessons", groupEn: "Chhath Knowledge Base" },
    { labelEn: "Gen Z & Kids Chhath Presentation", labelHi: "Gen Z और बच्चों की छठ प्रस्तुति", emoji: "🎤📊", link: "/chhathpuja-knowledge/youth-presentation", groupEn: "Chhath Knowledge Base" },

    // 🛠️ Puja Management
    { labelEn: "Complete Puja Checklist", labelHi: "पूर्ण पूजा चेकलिस्ट", emoji: "✅", link: "/chhathpuja-management/checklist", groupEn: "Puja Management" },
    { labelEn: "2026 Chhath Dates & Muhurat", labelHi: "2026 छठ तिथि एवं मुहूर्त", emoji: "📅", link: "/chhathpuja-management/dates-2026", groupEn: "Puja Management" },
    { labelEn: "14-Day Preparation Planner", labelHi: "14 दिन की तैयारी प्लानर", emoji: "📝", link: "/chhathpuja-management/preparation-planner", groupEn: "Puja Management" },
    { labelEn: "Family Puja Scheduler", labelHi: "परिवार पूजा शेड्यूलर", emoji: "👨‍👩‍👧‍👦", link: "/chhathpuja-management/family-planner", groupEn: "Puja Management" },
    { labelEn: "Puja Budget & Expense Tracker", labelHi: "पूजा बजट एवं खर्च ट्रैकर", emoji: "💰", link: "/chhathpuja-management/budget-tracker", groupEn: "Puja Management" },
    { labelEn: "Virtual Arghya Setup Simulator", labelHi: "वर्चुअल अर्घ्य सेटअप सिमुलेटर", emoji: "🌊", link: "/chhathpuja-management/virtual-arghya", groupEn: "Puja Management" },
    { labelEn: "Vendor & Pandit Booking", labelHi: "वेंडर एवं पंडित बुकिंग", emoji: "🛒", link: "/chhathpuja-management/book-vendors", groupEn: "Puja Management" },
    { labelEn: "Puja Reminder Alerts", labelHi: "पूजा रिमाइंडर अलर्ट", emoji: "⏰", link: "/chhathpuja-management/reminders", groupEn: "Puja Management" },
    { labelEn: "My Events List", labelHi: "मेरे कार्यक्रम", emoji: "📋🙏", link: "/event-flow-eventlist/my", groupEn: "Puja Management" },
    { labelEn: "Open Events List", labelHi: "खुले कार्यक्रम", emoji: "📖🌅", link: "/event-flow-eventlist/open", groupEn: "Puja Management" },
    { labelEn: "By Invitation Events", labelHi: "निमंत्रण द्वारा कार्यक्रम", emoji: "💌🪔", link: "/event-flow-eventlist/invitation", groupEn: "Puja Management" },

    // 🌞 Arghya & Rituals
    { labelEn: "Usha Arghya (Dawn) Guide", labelHi: "उषा अर्घ्य (सुबह) गाइड", emoji: "🌅", link: "/chhathpuja-rituals/usha-arghya", groupEn: "Arghya & Rituals" },
    { labelEn: "Sandhya Arghya (Dusk) Guide", labelHi: "संध्या अर्घ्य (शाम) गाइड", emoji: "🌇", link: "/chhathpuja-rituals/sandhya-arghya", groupEn: "Arghya & Rituals" },
    { labelEn: "Nadi Snan & Ghat Rituals", labelHi: "नदी स्नान एवं घाट अनुष्ठान", emoji: "🏞️", link: "/chhathpuja-rituals/nadi-snan", groupEn: "Arghya & Rituals" },
    { labelEn: "Offerings List (Thekua, Fruits, Sweets)", labelHi: "अर्पण सूची (ठेकुआ, फल, मिठाई)", emoji: "🍎", link: "/chhathpuja-rituals/offerings", groupEn: "Arghya & Rituals" },
    { labelEn: "Step-by-Step Puja Vidhi", labelHi: "चरणबद्ध पूजा विधि", emoji: "📖", link: "/chhathpuja-rituals/full-vidhi", groupEn: "Arghya & Rituals" },
    { labelEn: "Kosi & Bamboo Basket Setup", labelHi: "कोसी एवं बांस की टोकरी सेटअप", emoji: "🪔", link: "/chhathpuja-rituals/kosi-setup", groupEn: "Arghya & Rituals" },

    // 🍪 Thekua & Prasadam
    { labelEn: "Classic Bihari Thekua Recipe", labelHi: "क्लासिक बिहारी ठेकुआ रेसिपी", emoji: "🍪", link: "/chhathpuja-recipes/thekua-bihari", groupEn: "Thekua & Prasadam" },
    { labelEn: "East UP Style Thekua", labelHi: "पूर्वी उत्तर प्रदेश स्टाइल ठेकुआ", emoji: "🥜", link: "/chhathpuja-recipes/thekua-up", groupEn: "Thekua & Prasadam" },
    { labelEn: "Jharkhand Special Thekua", labelHi: "झारखंड स्पेशल ठेकुआ", emoji: "🌾", link: "/chhathpuja-recipes/thekua-jharkhand", groupEn: "Thekua & Prasadam" },
    { labelEn: "Halwa, Kheer & Other Prasadam", labelHi: "हलवा, खीर एवं अन्य प्रसाद", emoji: "🍚", link: "/chhathpuja-recipes/prasadam", groupEn: "Thekua & Prasadam" },
    { labelEn: "Video Recipe Tutorials", labelHi: "वीडियो रेसिपी ट्यूटोरियल", emoji: "📹", link: "/chhathpuja-recipes/video-recipes", groupEn: "Thekua & Prasadam" },
    { labelEn: "Family Thekua Recipe Book", labelHi: "परिवार ठेकुआ रेसिपी बुक", emoji: "📔", link: "/chhathpuja-recipes/family-book", groupEn: "Thekua & Prasadam" },
    { labelEn: "Healthy & Modern Thekua Variations", labelHi: "स्वास्थ्यवर्धक आधुनिक ठेकुआ विविधताएँ", emoji: "🥗", link: "/chhathpuja-recipes/healthy", groupEn: "Thekua & Prasadam" },

    // 🤝 Community Sangam
    { labelEn: "Chhath Puja Committee", labelHi: "छठ पूजा समिति", emoji: "🏛️🙏", link: "/community360/club-master", groupEn: "Community Sangam" },
    { labelEn: "Committee Members", labelHi: "समिति सदस्य", emoji: "👥🤝", link: "/community360/club-members", groupEn: "Community Sangam" },
    { labelEn: "Chanda / Donation", labelHi: "चंदा / दान", emoji: "🧾💰", link: "/community360/club-bills", groupEn: "Community Sangam" },    
    { labelEn: "Poorvanchal Community Forum", labelHi: "पूर्वांचल कम्युनिटी फोरम", emoji: "🗣️", link: "/chhathpuja-community/forum", groupEn: "Community Sangam" },
    { labelEn: "Connect with Bihar, UP, Jharkhand Devotees", labelHi: "बिहार, UP, झारखंड के भक्तों से जुड़ें", emoji: "🏡", link: "/chhathpuja-community/poorvanchal-sangam", groupEn: "Community Sangam" },
    { labelEn: "Local Chhath Groups Near You", labelHi: "आपके पास छठ समूह", emoji: "📍", link: "/chhathpuja-community/local-groups", groupEn: "Community Sangam" },
    { labelEn: "Bhojpuri Chhath Geet Sharing", labelHi: "भोजपुरी छठ गीत शेयरिंग", emoji: "🎤", link: "/chhathpuja-community/geet", groupEn: "Community Sangam" },
    { labelEn: "Women’s Chhath Circle", labelHi: "महिलाओं का छठ सर्कल", emoji: "👭", link: "/chhathpuja-community/womens-circle", groupEn: "Community Sangam" },
    { labelEn: "Elderly & Family Stories", labelHi: "बुजुर्गों एवं परिवार की कहानियाँ", emoji: "👵", link: "/chhathpuja-community/elderly", groupEn: "Community Sangam" },
    { labelEn: "Applause Hub – Celebrate Arghya", labelHi: "तालियाँ हब - अर्घ्य मनाएँ", emoji: "👏", link: "/chhathpuja-applause/hub", groupEn: "Community Sangam" },
    { labelEn: "Share Your Puja Photos & Videos", labelHi: "अपनी पूजा की फोटो और वीडियो शेयर करें", emoji: "📸", link: "/chhathpuja-applause/share-arghya", groupEn: "Community Sangam" },
    { labelEn: "Best Thekua & Setup Contest", labelHi: "बेस्ट ठेकुआ एवं सेटअप प्रतियोगिता", emoji: "🏆", link: "/chhathpuja-applause/contest", groupEn: "Community Sangam" },
    { labelEn: "Community Cheers & Blessings", labelHi: "समुदाय की शुभकामनाएँ", emoji: "🎉", link: "/chhathpuja-applause/cheers", groupEn: "Community Sangam" },
    { labelEn: "I Was Applauded", labelHi: "मुझे तालियाँ मिलीं", emoji: "🌟", link: "/chhathpuja-applause/for-me", groupEn: "Community Sangam" },

    // 📖 Chronicles & Kahani
    { labelEn: "Personal Chhath Stories", labelHi: "व्यक्तिगत छठ कहानियाँ", emoji: "📖", link: "/chhathpuja-chronicles/personal-stories", groupEn: "Chronicles & Kahani" },
    { labelEn: "Poorvanchal Chhath Chronicles", labelHi: "पूर्वांचल छठ वृत्तांत", emoji: "🏞️", link: "/chhathpuja-chronicles/poorvanchal-kahani", groupEn: "Chronicles & Kahani" },
    { labelEn: "Global Chhath Journeys", labelHi: "वैश्विक छठ यात्राएँ", emoji: "🌍", link: "/chhathpuja-chronicles/global-kahani", groupEn: "Chronicles & Kahani" },
    { labelEn: "Historical Records & Memories", labelHi: "ऐतिहासिक रिकॉर्ड एवं स्मृतियाँ", emoji: "🗞️", link: "/chhathpuja-chronicles/history", groupEn: "Chronicles & Kahani" },
    { labelEn: "Grandma’s Chhath Tales", labelHi: "दादी-नानी की छठ कहानियाँ", emoji: "👵📜", link: "/chhathpuja-chronicles/grandma", groupEn: "Chronicles & Kahani" },

    // 🛍️ Chhath Marketplace
    { labelEn: "Puja Items & Ghats Essentials", labelHi: "पूजा सामग्री एवं घाट आवश्यक सामान", emoji: "🪔", link: "/chhathpuja-marketplace/puja-items", groupEn: "Chhath Marketplace" },
    { labelEn: "Thekua, Sweets & Prasadam Shop", labelHi: "ठेकुआ, मिठाई एवं प्रसाद शॉप", emoji: "🍪", link: "/chhathpuja-marketplace/thekua-shop", groupEn: "Chhath Marketplace" },
    { labelEn: "Traditional Sarees & Attire", labelHi: "परंपरागत साड़ियाँ एवं वस्त्र", emoji: "🧣", link: "/chhathpuja-marketplace/attire", groupEn: "Chhath Marketplace" },
    { labelEn: "Home Delivery of Chhath Prasadam", labelHi: "छठ प्रसाद होम डिलीवरी", emoji: "🚚", link: "/chhathpuja-marketplace/delivery", groupEn: "Chhath Marketplace" },
    { labelEn: "Artisan Bamboo & Earthen Products", labelHi: "बांस एवं मिट्टी के हस्तशिल्प", emoji: "🪵", link: "/chhathpuja-marketplace/artisan", groupEn: "Chhath Marketplace" },
    { labelEn: "Export Chhath Items Worldwide", labelHi: "छठ सामग्री विश्व भर में निर्यात", emoji: "🌏", link: "/chhathpuja-marketplace/export", groupEn: "Chhath Marketplace" },
    { labelEn: "Set Budget & Get Delivery", labelHi: "बजट सेट करें और डिलीवरी पाएँ", emoji: "💰🚚", link: "/chhathpuja-marketplace/budget-delivery", groupEn: "Chhath Marketplace" },
    { labelEn: "Open Digital Seva Shop", labelHi: "डिजिटल सेवा शॉप खोलें", emoji: "🛒💻", link: "/chhathpuja-marketplace/digital-seva-shop", groupEn: "Chhath Marketplace" },
    { labelEn: "Need Instantly", labelHi: "तुरंत चाहिए", emoji: "⚡🛍️", link: "/chhathpuja-marketplace/instant-need", groupEn: "Chhath Marketplace" },
    { labelEn: "Become Chhath Products Seller", labelHi: "छठ उत्पाद विक्रेता बनें", emoji: "🛍️👩‍💼", link: "/chhathpuja-marketplace/become-seller", groupEn: "Chhath Marketplace" },
    { labelEn: "Become Chhath Service Provider", labelHi: "छठ सेवा प्रदाता बनें", emoji: "🛠️🙏", link: "/chhathpuja-marketplace/become-service-provider", groupEn: "Chhath Marketplace" },

    // 📺 ChhathTube
    { labelEn: "Live Arghya Streams from Ghats", labelHi: "घाटों से लाइव अर्घ्य स्ट्रीम", emoji: "🌞", link: "/chhathpuja-tube/live-arghya", groupEn: "ChhathTube" },
    { labelEn: "ChhathTube Home – Devotional Videos", labelHi: "छठट्यूब होम - भक्ति वीडियो", emoji: "▶️", link: "/chhathpuja-tube/home", groupEn: "ChhathTube" },
    { labelEn: "Bhojpuri Chhath Songs & Bhajans", labelHi: "भोजपुरी छठ गाने एवं भजन", emoji: "🎵", link: "/chhathpuja-tube/songs", groupEn: "ChhathTube" },
    { labelEn: "User Arghya Videos", labelHi: "उपयोगकर्ता अर्घ्य वीडियो", emoji: "📹", link: "/chhathpuja-tube/user-videos", groupEn: "ChhathTube" },
    { labelEn: "Documentaries on Chhath Puja", labelHi: "छठ पूजा पर डॉक्यूमेंट्री", emoji: "🎬", link: "/chhathpuja-tube/documentaries", groupEn: "ChhathTube" },
    { labelEn: "Upload Your Chhath Moments", labelHi: "अपने छठ पलों को अपलोड करें", emoji: "📤", link: "/chhathpuja-tube/upload", groupEn: "ChhathTube" },

    // 🌍 Global Chhath
    { labelEn: "Chhath in Mumbai & Maharashtra", labelHi: "मुंबई एवं महाराष्ट्र में छठ", emoji: "🏙️", link: "/chhathpuja-global/mumbai", groupEn: "Global Chhath" },
    { labelEn: "Diaspora Celebrations Worldwide", labelHi: "विदेशों में छठ उत्सव", emoji: "🌏", link: "/chhathpuja-global/diaspora", groupEn: "Global Chhath" },
    { labelEn: "International Chhath Sangam", labelHi: "अंतरराष्ट्रीय छठ संगम", emoji: "🤝", link: "/chhathpuja-global/international", groupEn: "Global Chhath" },
    { labelEn: "Chhath in USA, UK, Canada & Beyond", labelHi: "अमेरिका, ब्रिटेन, कनाडा एवं अन्य देशों में छठ", emoji: "✈️", link: "/chhathpuja-global/abroad", groupEn: "Global Chhath" },

    // 📅 Festival Calendar
    { labelEn: "Chhath 2026 Full Schedule", labelHi: "2026 छठ पूर्ण कार्यक्रम", emoji: "📆", link: "/chhathpuja-calendar/2026", groupEn: "Festival Calendar" },
    { labelEn: "Upcoming Chhath & Related Festivals", labelHi: "आगामी छठ एवं संबंधित त्योहार", emoji: "🗓️", link: "/chhathpuja-calendar/upcoming", groupEn: "Festival Calendar" },
    { labelEn: "Lunar Tithi & Muhurat Tracker", labelHi: "चंद्र तिथि एवं मुहूर्त ट्रैकर", emoji: "🌕", link: "/chhathpuja-calendar/lunar", groupEn: "Festival Calendar" },

    // 🙏 Live Darshan & Events
    { labelEn: "Live Ghat Darshan (Patna, Varanasi, Ranchi)", labelHi: "लाइव घाट दर्शन (पटना, वाराणसी, रांची)", emoji: "🌊", link: "/chhathpuja-live/ghats", groupEn: "Live Darshan & Events" },
    { labelEn: "Virtual Temple & River Darshan", labelHi: "वर्चुअल मंदिर एवं नदी दर्शन", emoji: "🛕", link: "/chhathpuja-live/virtual-darshan", groupEn: "Live Darshan & Events" },
    { labelEn: "Community Chhath Events Near You", labelHi: "आपके पास छठ समुदाय आयोजन", emoji: "📍", link: "/chhathpuja-live/events", groupEn: "Live Darshan & Events" },

    // 🏅 Recognition & Progress
    { labelEn: "Chhath Devotee Ranking", labelHi: "छठ भक्त रैंकिंग", emoji: "🏆", link: "/chhathpuja-recognition/ranking", groupEn: "Recognition & Progress" },
    { labelEn: "Best Arghya & Story Awards", labelHi: "बेस्ट अर्घ्य एवं कहानी पुरस्कार", emoji: "🌟", link: "/chhathpuja-recognition/awards", groupEn: "Recognition & Progress" },
    { labelEn: "Chhath Progress Dashboard", labelHi: "छठ प्रगति डैशबोर्ड", emoji: "📈", link: "/chhathpuja-recognition/progress", groupEn: "Recognition & Progress" },

    // 📊 Analytics & Insights
    { labelEn: "Global Participation Stats", labelHi: "वैश्विक भागीदारी आँकड़े", emoji: "🌍📊", link: "/chhathpuja-analytics/stats", groupEn: "Analytics & Insights" },
    { labelEn: "Community Insights & Trends", labelHi: "समुदाय अंतर्दृष्टि एवं ट्रेंड", emoji: "🔍", link: "/chhathpuja-analytics/insights", groupEn: "Analytics & Insights" },
 
    // 💰 Employment & Income
    { labelEn: "My Income Credit", labelHi: "मेरी आय क्रेडिट", emoji: "🌱", link: "/chhathpuja-earning/my-income-credits", groupEn: "Employment & Income" },
    { labelEn: "My Income", labelHi: "मेरी आय", emoji: "💰", link: "/chhathpuja-earning/my-income", groupEn: "Employment & Income" },
    { labelEn: "Income Estimation", labelHi: "आय अनुमान", emoji: "📈", link: "/chhathpuja-earning/income-estimator", groupEn: "Employment & Income" },
    { labelEn: "Chhath Kharcho Jeeto", labelHi: "छठ खर्चो जीतो", emoji: "🏆", link: "/chhathpuja-earning/kharchojeeto", groupEn: "Employment & Income" },
    { labelEn: "Photo Can Win", labelHi: "अर्घ्य फोटो से जीतें", emoji: "📸🌞", link: "/chhathpuja-earning/photocanwin", groupEn: "Employment & Income" },
    { labelEn: "Become Chhath Products Reseller", labelHi: "छठ उत्पाद रीसेलर बनें", emoji: "♻️🛍️", link: "/chhathpuja-earning/become-reseller", groupEn: "Employment & Income" },
    { labelEn: "Rewarding Chhath Ads", labelHi: "छठ पुरस्कृत विज्ञापन", emoji: "🎁🙏", link: "/chhathpuja-earning/rewarding-ads", groupEn: "Employment & Income" },
    { labelEn: "Watch Rewarding Videos", labelHi: "पुरस्कृत छठ वीडियो देखें", emoji: "🎥💰", link: "/chhathpuja-earning/rewarding-videos", groupEn: "Employment & Income" },
    { labelEn: "Create Digital Chhath Boutique", labelHi: "डिजिटल छठ बुटिक बनाएँ", emoji: "🛍️🪔", link: "/chhathpuja-earning/digital-boutique", groupEn: "Employment & Income" },
];

  // Get current active group
  const currentGroupEn = language === "en" 
    ? activeTab 
    : groupNames.find(g => g.labelHi === activeTab)?.labelEn || "Chhath Knowledge Base";

  const filteredButtons = menuButtons1.filter((btn) => btn.groupEn === currentGroupEn);

  const displayGroupName = (group) => (language === "en" ? group.labelEn : group.labelHi);
  const displayButtonLabel = (btn) => (language === "en" ? btn.labelEn : btn.labelHi);

  return (
    <div className="min-h-screen flex flex-col bg-amber-950 text-amber-100 font-sans">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-amber-950 border-b border-orange-800 px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex flex-col">
          <div className="text-xl lg:text-2xl font-bold text-orange-400">{t.title}</div>
          <div className="text-[10px] lg:text-sm text-yellow-300">{t.subtitle}</div>
          <div className="text-[9px] lg:text-xs text-yellow-300 mt-0.5">{t.tagline}</div>
        </div>

        <div className="flex items-center gap-4">
          {/* Language Toggle */}
          <div className="flex border border-orange-600 rounded-full overflow-hidden text-xs font-medium shadow-sm">
            <button
              onClick={toggleLanguage}
              className={`px-4 py-1.5 transition-all ${language === "en" ? "bg-orange-600 text-white" : "text-amber-200 hover:bg-orange-900"}`}
            >
              EN
            </button>
            <button
              onClick={toggleLanguage}
              className={`px-4 py-1.5 transition-all ${language === "hi" ? "bg-orange-600 text-white" : "text-amber-200 hover:bg-orange-900"}`}
            >
              हिंदी
            </button>
          </div>

          {isLoggedIn ? (
            <div className="hidden md:flex items-center gap-3">
              <div className="text-xs text-amber-300">{t.welcome} {userName}</div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-md bg-red-600 hover:bg-red-700 transition text-white"
                title={t.logout}
              >
                ⏻
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="hidden md:block px-5 py-2 rounded-full border border-orange-700 text-sm text-amber-200 hover:bg-orange-900 transition"
            >
              {t.login}
            </button>
          )}

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-9 h-9 flex items-center justify-center text-2xl text-amber-300 bg-orange-600 rounded-lg"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "✕" : "☰"}
          </button>
        </div>
      </header>

      {/* Top Quick Links */}
      <div className="bg-orange-900/40 text-white text-xs sm:text-sm py-2.5">
        <div className="mx-auto px-4 flex flex-wrap justify-center gap-x-4 gap-y-1">
          <a href="/chhathpuja-about" className="hover:text-yellow-200 transition">{t.about}</a>
          <a href="/chhathpuja-live-dates" className="hover:text-yellow-300 transition">{t.liveDates}</a>
          <Link to="/chhathpuja-tube" className="hover:text-yellow-200 transition">{t.chhathTube}</Link>
          <a href="/chhathpuja-updates" className="hover:text-yellow-200 transition">{t.updates}</a>
          <a href="/chhathpuja-faq" className="hover:text-yellow-200 transition">{t.faq}</a>
          <a href="/chhathpuja-ai" className="hover:text-yellow-200 transition">{t.askAI}</a>
        </div>
        <div className="text-center text-[10px] lg:text-xs text-amber-200/80 mt-2 px-4">
          {t.description}
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 py-4 border-b border-orange-700 bg-amber-950">
        <div className="grid grid-cols-3 lg:grid-cols-7 gap-1">
          {groupNames.map((group, idx) => (
            <button
              key={idx}
              onClick={() => setActiveTab(language === "en" ? group.labelEn : group.labelHi)}
              className={`flex flex-col items-center justify-center p-1 rounded-xl text-xs sm:text-sm font-medium transition-all border ${
                (language === "en" ? activeTab === group.labelEn : activeTab === group.labelHi)
                  ? "bg-orange-700 border-orange-500 text-yellow-200 shadow-md"
                  : "bg-orange-900/60 border-orange-800 hover:bg-orange-800 text-amber-100"
              }`}
            >
              <span className="text-xl mb-2">{group.emoji}</span>
              <span className="tetx-xs lg:text-sm text-center leading-tight">{displayGroupName(group)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <main className="flex-grow px-4 py-2 w-full">
        <div className="grid grid-cols-3 lg:grid-cols-7 gap-1">
          {filteredButtons.length === 0 ? (
            <div className="col-span-full text-center py-4 text-orange-400 text-lg">
              {t.noItems}
            </div>
          ) : (
            filteredButtons.map((btn, idx) => (
              <button
                key={idx}
                onClick={() => navigate(btn.link)}
                className="group flex flex-col items-center justify-center p-1 rounded-xl bg-gradient-to-br from-orange-900/40 to-amber-950 border border-orange-800 hover:border-orange-600 hover:bg-orange-800/60 transition-all duration-300 hover:scale-105 active:scale-95"
              >
                <span className="text-xl mb-4 transition-transform group-hover:scale-110">{btn.emoji}</span>
                <span className="text-xs lg:text-sm font-medium text-center leading-tight px-2">
                  {displayButtonLabel(btn)}
                </span>
              </button>
            ))
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-amber-950 border-t border-orange-800 text-amber-200 py-6 mt-auto">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
          <div className="text-center md:text-left">{t.footerText}</div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/about-us" className="text-gray-100 hover:text-orange-300 transition">{t.aboutLink}</Link>
            <Link to="/privacy-policy" className="text-gray-100 hover:text-orange-300 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-gray-100 hover:text-orange-300 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-gray-100 hover:text-orange-300 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ChhathPujaHome;